// Play styles for the chord chart: the analyzed chords played another way — a drone ringing over them
// (voicings.ts), a walking bass through slash chords, power chords, or triads on the top three strings.
// Every style is a playing suggestion; what is shown keeps the analyzed chord readable. Pure, no DOM.
// Labels are shapes (sharp-canonical, after chordView's shift); frets are [s6..s1], -1 muted, 0 open.

import { NOTE_NAMES, OPEN_MIDI, chordShape } from "./chords";
import { barreCount, transposeChord, type KeyQuality, type KeySetting } from "./transpose";
import { DRONES, fingersNeeded, pickDrone, shapeKey, suggestVoicings, type DroneId } from "./voicings";

export type PlayStyle = "orig" | "drone" | "walk" | "power" | "triad";
export const PLAY_STYLES: PlayStyle[] = ["orig", "drone", "walk", "power", "triad"];

/** a chord as a style plays it */
export interface Played {
  name: string;          // what sounds: "C#m7", "D/F#", "E5"
  added: string;         // the trailing part the style added ("7", "/F#", "5"); "" when nothing was added
  from: string | null;   // the analyzed chord when the name no longer shows it ("Em" under "E5")
  frets: number[];
  marks: number[];       // strings to point out: the drone that keeps ringing, the new bass
}

/** a style that fits the song; `capo` is the capo it needs when the current one doesn't work for it */
export interface StyleOption { style: PlayStyle; capo: number | null; recommended: boolean }

interface Key { tonic: string; quality: KeyQuality }

const TONES: Record<string, number[]> = {
  "": [0, 4, 7], m: [0, 3, 7], 7: [0, 4, 7, 10], m7: [0, 3, 7, 10], maj7: [0, 4, 7, 11], 6: [0, 4, 7, 9],
  m6: [0, 3, 7, 9], mM7: [0, 3, 7, 11], sus2: [0, 2, 7], sus4: [0, 5, 7], dim: [0, 3, 6], aug: [0, 4, 8],
  dim7: [0, 3, 6, 9], m7b5: [0, 3, 6, 10],
};
const MAJOR_SCALE = [0, 2, 4, 5, 7, 9, 11];
const MINOR_SCALE = [0, 2, 3, 5, 7, 8, 10];
const CAPO_TRY_MAX = 5;          // like the capo suggestion: higher capos are rarely used
const MAX_BASS_FRET = 7;
const TOP3 = [3, 4, 5];          // string indices of s3, s2, s1

const mod12 = (x: number) => ((x % 12) + 12) % 12;
const midiAt = (i: number, fret: number) => OPEN_MIDI[6 - i] + fret;
/** shortest signed move between pitch classes, −5..6 */
const step = (a: number, b: number) => { const d = mod12(b - a); return d > 6 ? d - 12 : d; };
const isStep = (d: number) => Math.abs(d) >= 1 && Math.abs(d) <= 2;
const lexLess = (a: number[], b: number[]) => {
  for (let k = 0; k < a.length; k++) if (a[k] !== b[k]) return a[k] < b[k];
  return false;
};

function parse(label: string): { root: number; tones: number[] } | null {
  const m = /^([A-G]#?)(.*)$/.exec(label);
  const tones = m ? TONES[m[2]] : undefined;
  return m && tones ? { root: NOTE_NAMES.indexOf(m[1]), tones } : null;
}

function played(label: string, name: string, frets: number[], marks: number[] = []): Played {
  if (name.startsWith(label)) return { name, added: name.slice(label.length), from: null, frets, marks };
  const root = /^[A-G]#?/.exec(name)?.[0] ?? "";
  return { name, added: name.slice(root.length), from: label, frets, marks };
}

function plain(label: string): Played | null {
  const sh = chordShape(label);
  return sh ? { name: label, added: "", from: null, frets: sh.frets, marks: [] } : null;
}

const uniq = (labels: (string | null)[]) => [...new Set(labels.filter((l): l is string => !!l))];

// ---- drone: open B·E or D·G at the 3rd fret ring through every chord (voicings.ts) ----

function droneChart(labels: (string | null)[], key: Key): (Played | null)[] {
  const drone = pickDrone(uniq(labels), key);
  const cache = new Map<string, Played | null>();
  const one = (label: string) => {
    const v = drone ? suggestVoicings(label, key, { drone, limit: 1 })[0] : undefined;
    return v ? played(label, v.name, v.frets, droneMarks(v.frets, drone!)) : plain(label);
  };
  return labels.map((l) => {
    if (!l) return null;
    if (!cache.has(l)) cache.set(l, one(l));
    return cache.get(l)!;
  });
}

function droneMarks(frets: number[], drone: DroneId): number[] {
  return DRONES[drone].flatMap((f, i) => (f >= 0 && frets[i] === f ? [i] : []));
}

/** the drone the chart would use, and how many of its chords get a voicing from it */
function droneCoverage(labels: (string | null)[], key: Key): { drone: DroneId | null; covered: number; total: number } {
  const chords = uniq(labels);
  const drone = pickDrone(chords, key);
  const covered = drone ? chords.filter((c) => suggestVoicings(c, key, { drone, limit: 1 }).length).length : 0;
  return { drone, covered, total: chords.length };
}
const droneFits = (c: { covered: number; total: number }) => c.covered > 0 && c.covered * 2 >= c.total;

// ---- walking bass: put the 3rd or 5th in the bass where that makes the bass line move by step ----

/** the standard shape with its bass moved to `bass` (D → D/F# [2,x,0,2,3,2]); null when no playable one */
export function slashShape(label: string, bass: number): number[] | null {
  const std = chordShape(label)?.frets;
  const p = parse(label);
  if (!std || !p) return null;
  const third = p.tones.find((i) => i === 3 || i === 4);
  let best: { frets: number[]; cost: number[] } | null = null;
  for (let b = 0; b <= 2; b++) {
    for (let f = 0; f <= MAX_BASS_FRET; f++) {
      if (mod12(midiAt(b, f)) !== bass) continue;
      const frets = std.map((x, i) => (i < b ? -1 : i === b ? f : x));
      const notes = frets.flatMap((x, i) => (x >= 0 ? [midiAt(i, x)] : []));
      if (notes.length < 4 || Math.min(...notes) < midiAt(b, f)) continue;
      const pcs = new Set(notes.map((n) => mod12(n - p.root)));
      if (!pcs.has(0) || (third != null && !pcs.has(third))) continue;
      const fretted = frets.filter((x) => x > 0);
      if (fretted.length && Math.max(...fretted) - Math.min(...fretted) > 3) continue;
      const fingers = fingersNeeded(frets);
      if (fingers == null || fingers > 4) continue;
      // closest to the familiar shape, then fuller, fewer fingers, lower on the neck, a lower bass string
      const cost = [frets.filter((x, i) => x !== std[i]).length, -notes.length, fingers, Math.max(0, ...fretted), b];
      if (!best || lexLess(cost, best.cost)) best = { frets, cost };
    }
  }
  return best?.frets ?? null;
}

function walkChart(labels: (string | null)[], key: Key): (Played | null)[] {
  const out = labels.map((l) => (l ? plain(l) : null));
  const runs: { label: string; at: number[] }[] = [];
  labels.forEach((l, i) => {
    if (!l) return;
    const last = runs[runs.length - 1];
    if (last && last.label === l) last.at.push(i);
    else runs.push({ label: l, at: [i] });
  });
  const tonic = NOTE_NAMES.indexOf(key.tonic);
  const scale = new Set((key.quality === "minor" ? MINOR_SCALE : MAJOR_SCALE).map((i) => mod12(tonic + i)));
  let prevBass: number | null = null;
  let prevMoved = false;
  runs.forEach((run, r) => {
    const p = parse(run.label);
    const next = runs[r + 1] ? parse(runs[r + 1].label) : null;
    let bass = p?.root ?? null;
    // a passing bass needs both neighbours, an unmoved chord before it, and a leap to smooth out
    if (p && next && prevBass != null && !prevMoved && !(isStep(step(prevBass, p.root)) && isStep(step(p.root, next.root)))) {
      let pick: { bass: number; frets: number[]; move: number } | null = null;
      for (const iv of p.tones.filter((i) => i === 3 || i === 4 || i === 7)) {
        const b = mod12(p.root + iv);
        const d1 = step(prevBass, b), d2 = step(b, next.root);
        if (!isStep(d1) || !isStep(d2) || Math.sign(d1) !== Math.sign(d2) || !scale.has(b)) continue;
        const frets = slashShape(run.label, b);
        if (frets && (!pick || Math.abs(d1) + Math.abs(d2) < pick.move)) pick = { bass: b, frets, move: Math.abs(d1) + Math.abs(d2) };
      }
      if (pick) {
        bass = pick.bass;
        const marks = [pick.frets.findIndex((f) => f >= 0)];
        for (const i of run.at) out[i] = played(run.label, `${run.label}/${NOTE_NAMES[pick.bass]}`, pick.frets, marks);
      }
    }
    prevMoved = p != null && bass !== p.root;
    prevBass = bass;
  });
  return out;
}

// ---- power chords: root and 5th, no barre ----

const POWER: [number, number[]][] = [   // root string index, shape with the root fret at 0
  [0, [0, 2, 2, -1, -1, -1]], [1, [-1, 0, 2, 2, -1, -1]], [2, [-1, -1, 0, 2, 3, -1]],
];

export function powerShape(label: string): Played | null {
  const p = parse(label);
  if (!p || !p.tones.includes(7)) return null;   // dim / aug / m7b5 keep their own shape
  let best: number[] | null = null;
  for (const [s, tpl] of POWER) {
    const f = mod12(p.root - OPEN_MIDI[6 - s]);
    const frets = tpl.map((o) => (o < 0 ? -1 : o + f));
    if (!best || Math.max(...frets) < Math.max(...best)) best = frets;
  }
  return played(label, NOTE_NAMES[p.root] + "5", best!);
}

// ---- top-three-string triads, each placed close to the one before ----

function triadTones(tones: number[]): number[] {
  if (tones.length === 3) return tones;
  return tones.includes(7) ? tones.filter((i) => i !== 7) : tones.slice(0, 3);   // drop a plain 5th first
}

function triadPlaces(label: string): number[][] {
  const p = parse(label);
  if (!p) return [];
  const want = triadTones(p.tones).map((i) => mod12(p.root + i));
  const out: number[][] = [];
  for (let a = 0; a <= 12; a++) for (let b = 0; b <= 12; b++) for (let c = 0; c <= 12; c++) {
    const top = [a, b, c];
    const pcs = top.map((f, k) => mod12(midiAt(TOP3[k], f)));
    if (new Set(pcs).size !== 3 || !want.every((w) => pcs.includes(w))) continue;
    if (Math.max(...top) - Math.min(...top) > 3) continue;
    const frets = [-1, -1, -1, ...top];
    const fingers = fingersNeeded(frets);
    if (fingers != null && fingers <= 3) out.push(frets);
  }
  return out;
}

function triadChart(labels: (string | null)[]): (Played | null)[] {
  const out = labels.map((l) => (l ? plain(l) : null));
  const runs: { label: string; at: number[]; places: number[][] }[] = [];
  labels.forEach((l, i) => {
    if (!l) return;
    const last = runs[runs.length - 1];
    if (last && last.label === l) last.at.push(i);
    else runs.push({ label: l, at: [i], places: triadPlaces(l) });
  });
  const move = (x: number[], y: number[]) => TOP3.reduce((a, i) => a + Math.abs(x[i] - y[i]), 0);
  const height = (x: number[]) => 0.2 * TOP3.reduce((a, i) => a + x[i], 0) / 3;
  // least total hand movement (plus a nudge toward the lower neck) over each stretch of placeable chords
  let k = 0;
  while (k < runs.length) {
    if (!runs[k].places.length) { k++; continue; }
    let end = k;
    while (end < runs.length && runs[end].places.length) end++;
    const cost: number[][] = [], back: number[][] = [];
    for (let r = k; r < end; r++) {
      const pl = runs[r].places;
      cost.push(pl.map((x, j) => {
        if (r === k) return height(x);
        const prev = runs[r - 1].places, pc = cost[r - 1 - k];
        let best = Infinity, arg = 0;
        prev.forEach((y, q) => { const c = pc[q] + move(y, x); if (c < best) { best = c; arg = q; } });
        (back[r - k] ??= [])[j] = arg;
        return best + height(x);
      }));
    }
    let j = cost[end - 1 - k].indexOf(Math.min(...cost[end - 1 - k]));
    for (let r = end - 1; r >= k; r--) {
      const frets = runs[r].places[j];
      for (const i of runs[r].at) out[i] = { name: runs[r].label, added: "", from: null, frets, marks: [] };
      if (r > k) j = back[r - k][j];
    }
    k = end;
  }
  return out;
}

// ---- the chart and the choice of styles ----

/** how each chord of a chart (in order; null = no chord) is played in a style; null where nothing can be shown */
export function styleChart(labels: (string | null)[], key: Key, style: PlayStyle): (Played | null)[] {
  switch (style) {
    case "drone": return droneChart(labels, key);
    case "walk": return walkChart(labels, key);
    case "power": return labels.map((l) => (l ? powerShape(l) ?? plain(l) : null));
    case "triad": return triadChart(labels);
    default: return labels.map((l) => (l ? plain(l) : null));
  }
}

/** the drone a chart uses in a style (for wording), null when it isn't the drone style or none fits */
export function chartDrone(labels: (string | null)[], key: Key): DroneId | null {
  return pickDrone(uniq(labels), key);
}

/** the styles worth offering for a song (sounding labels, in order) at a key setting. Only what fits:
 *  the drone when it reaches at least half of the chords — at another capo if need be —, the walking bass when
 *  it has a place to walk, power chords when there is a barre to save. "추천" goes to the drone, else the walk. */
export function styleOptions(labels: (string | null)[], songKey: Key, setting: KeySetting): StyleOption[] {
  const at = (capo: number) => {
    const shift = setting.transpose - capo;
    return { shapes: labels.map((l) => (l ? transposeChord(l, shift) : null)), key: shapeKey(songKey, shift), shift };
  };
  const now = at(setting.capo);
  const opts: StyleOption[] = [{ style: "orig", capo: null, recommended: false }];

  if (droneFits(droneCoverage(now.shapes, now.key))) opts.push({ style: "drone", capo: null, recommended: false });
  else {
    let best: { capo: number; covered: number; barre: number } | null = null;
    for (let capo = 0; capo <= CAPO_TRY_MAX; capo++) {
      if (capo === setting.capo) continue;
      const s = at(capo);
      const c = droneCoverage(s.shapes, s.key);
      if (!droneFits(c)) continue;
      const cand = { capo, covered: c.covered, barre: barreCount(uniq(labels), s.shift) };
      const dist = (x: { capo: number }) => Math.abs(x.capo - setting.capo);
      if (!best || cand.covered > best.covered || (cand.covered === best.covered
        && (cand.barre < best.barre || (cand.barre === best.barre && dist(cand) < dist(best))))) best = cand;
    }
    if (best) opts.push({ style: "drone", capo: best.capo, recommended: false });
  }
  const walked = walkChart(now.shapes, now.key);
  if (walked.some((p) => p?.added.startsWith("/"))) opts.push({ style: "walk", capo: null, recommended: false });
  if (barreCount(uniq(labels), now.shift) > 0) opts.push({ style: "power", capo: null, recommended: false });
  if (uniq(now.shapes).some((l) => triadPlaces(l).length)) opts.push({ style: "triad", capo: null, recommended: false });
  const rec = opts.find((o) => o.style === "drone") ?? opts.find((o) => o.style === "walk");
  if (rec) rec.recommended = true;
  return opts;
}
